/**
 * GL-DESIGN-V3-2026Q4 — "Instrument" neumorphic component kit.
 *
 * These components form the complete v3 primitive set. Build pages by
 * composing them — do NOT re-create raised/inset shadows inline. All
 * raised/inset elements MUST sit on `var(--v3-surface)` or the effect
 * breaks.
 *
 * Full-page example:
 *   <div className="v3-surface min-h-screen">
 *     <PrimaryPill icon="plus" onClick={...}>New engagement</PrimaryPill>
 *     <Card><ScoreDial pct={63} /></Card>
 *   </div>
 */
import { forwardRef } from 'react';
import * as Ph from '@phosphor-icons/react';

// -----------------------------------------------------------------
// Shadow / layout utility (so pages don't repeat the tokens inline)
// -----------------------------------------------------------------
export const V3 = {
  surface: 'var(--v3-surface)',
  tint: 'var(--v3-tint)',
  navy: 'var(--v3-navy)',
  navyDeep: 'var(--v3-navy-deep)',
  gold: 'var(--v3-gold)',
  goldDim: 'var(--v3-gold-dim)',
  midNavy: 'var(--v3-mid-navy)',
  ink: 'var(--v3-ink)',
  muted: 'var(--v3-muted)',
  p1: 'var(--v3-p1)',
  p2: 'var(--v3-p2)',
  p3: 'var(--v3-p3)',
  mono: "'JetBrains Mono', ui-monospace, monospace",
  raised: 'var(--v3-shadow-raised)',
  raisedLg: 'var(--v3-shadow-raised-lg)',
  raisedSm: 'var(--v3-shadow-raised-sm)',
  inset: 'var(--v3-shadow-inset)',
  insetSm: 'var(--v3-shadow-inset-sm)',
  navyBtn: 'var(--v3-shadow-navy-btn)',
  navyCard: 'var(--v3-shadow-navy-card)',
};

const PhIcon = ({ name, size = 18, weight = 'bold', color }) => {
  // Convert "arrow-right" → "ArrowRight"
  const comp = name
    .split('-')
    .map((p) => p.charAt(0).toUpperCase() + p.slice(1))
    .join('');
  const Icon = Ph[comp];
  if (!Icon) return null;
  return <Icon size={size} weight={weight} color={color} />;
};

// -----------------------------------------------------------------
// PrimaryPill — Navy filled pill, white label left, white circle right
// -----------------------------------------------------------------
export const PrimaryPill = forwardRef(function PrimaryPill(
  { children, icon = 'plus', iconWeight = 'bold', onClick, height = 52, testid, className = '', style = {} },
  ref
) {
  const circle = Math.max(38, height - 14);
  return (
    <button
      ref={ref}
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={className}
      style={{
        height,
        padding: `0 8px 0 ${Math.max(22, height / 3)}px`,
        borderRadius: 999,
        background: V3.navy,
        color: '#fff',
        fontWeight: 600,
        fontSize: height >= 60 ? 16 : 15,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 14,
        cursor: 'pointer',
        border: 0,
        boxShadow: V3.navyBtn,
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      <span>{children}</span>
      <span
        style={{
          width: circle,
          height: circle,
          borderRadius: '50%',
          background: '#fff',
          color: V3.navy,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: Math.max(16, circle / 2.4),
        }}
      >
        <PhIcon name={icon} size={Math.max(16, circle / 2.4)} weight={iconWeight} />
      </span>
    </button>
  );
});

// -----------------------------------------------------------------
// SendPill — tall navy pill, right side is a white pill with 3 carets
// -----------------------------------------------------------------
export function SendPill({ children = 'Review & send to client', onClick, testid, height = 68 }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      style={{
        height,
        width: '100%',
        padding: '0 10px 0 26px',
        borderRadius: 999,
        background: V3.navy,
        color: '#fff',
        fontWeight: 600,
        fontSize: 17,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: 'pointer',
        border: 0,
        boxShadow: V3.navyBtn,
      }}
    >
      <span>{children}</span>
      <span
        style={{
          height: 48,
          padding: '0 18px',
          borderRadius: 999,
          background: '#fff',
          color: V3.navy,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <PhIcon name="caret-right" size={18} />
        <span style={{ opacity: 0.6 }}><PhIcon name="caret-right" size={18} /></span>
        <span style={{ opacity: 0.3 }}><PhIcon name="caret-right" size={18} /></span>
      </span>
    </button>
  );
}

// -----------------------------------------------------------------
// OutlinePill — 1.5px navy border, navy label, no fill
// -----------------------------------------------------------------
export function OutlinePill({ children, onClick, icon, testid, height = 44, className = '' }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={className}
      style={{
        height,
        padding: '0 18px',
        borderRadius: 999,
        background: 'transparent',
        color: V3.navy,
        fontWeight: 600,
        fontSize: 14,
        border: `1.5px solid ${V3.navy}`,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        cursor: 'pointer',
      }}
    >
      {icon && <PhIcon name={icon} size={16} />}
      <span>{children}</span>
    </button>
  );
}

// -----------------------------------------------------------------
// IconButton — 44/56px circle; raised (surface) or tint variant
// -----------------------------------------------------------------
export function IconButton({ icon, onClick, size = 44, tint = false, testid, title, color }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      title={title}
      aria-label={title}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: tint ? V3.tint : V3.surface,
        color: color || V3.navy,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: 0,
        cursor: 'pointer',
        boxShadow: tint ? 'none' : V3.raisedSm,
        flexShrink: 0,
      }}
    >
      <PhIcon name={icon} size={Math.max(16, size / 2.5)} />
    </button>
  );
}

// -----------------------------------------------------------------
// Segmented — inset track, active segment is a navy pill
// -----------------------------------------------------------------
export function Segmented({ options, value, onChange, testid, size = 'md' }) {
  const h = size === 'sm' ? 28 : 34;
  return (
    <div
      data-testid={testid}
      style={{
        display: 'inline-flex',
        gap: 4,
        background: V3.surface,
        borderRadius: 999,
        padding: 5,
        boxShadow: V3.insetSm,
      }}
    >
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange?.(opt.value)}
            data-testid={testid ? `${testid}-${opt.value}` : undefined}
            style={{
              height: h,
              padding: '0 14px',
              borderRadius: 999,
              background: active ? V3.navy : 'transparent',
              color: active ? '#fff' : V3.muted,
              fontSize: 13,
              fontWeight: 600,
              border: 0,
              cursor: 'pointer',
              boxShadow: active ? '0 4px 8px -4px rgba(20,38,84,.4)' : 'none',
              whiteSpace: 'nowrap',
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

// -----------------------------------------------------------------
// Toggle — 54×32 track, 24px white knob
// -----------------------------------------------------------------
export function Toggle({ value, onChange, testid, disabled = false }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={value}
      disabled={disabled}
      onClick={() => !disabled && onChange?.(!value)}
      data-testid={testid}
      style={{
        width: 54,
        height: 32,
        borderRadius: 16,
        background: value ? V3.navy : V3.surface,
        boxShadow: value
          ? 'inset 0 1px 3px rgba(0,0,0,.3)'
          : V3.insetSm,
        position: 'relative',
        cursor: disabled ? 'not-allowed' : 'pointer',
        flexShrink: 0,
        border: 0,
        opacity: disabled ? 0.5 : 1,
        transition: 'background 160ms ease',
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 4,
          left: value ? 26 : 4,
          width: 24,
          height: 24,
          borderRadius: '50%',
          background: '#fff',
          boxShadow: V3.shadowToggleKnob || '2px 2px 5px rgba(20,38,84,.25)',
          transition: 'left 160ms ease',
        }}
      />
    </button>
  );
}

// -----------------------------------------------------------------
// Card — surface fill, radius 22, raised shadow
// -----------------------------------------------------------------
export const Card = forwardRef(function Card(
  { children, padding = 18, radius = 22, className = '', style = {}, testid, as: As = 'div', onClick },
  ref
) {
  return (
    <As
      ref={ref}
      data-testid={testid}
      className={className}
      onClick={onClick}
      style={{
        background: V3.surface,
        borderRadius: radius,
        boxShadow: V3.raised,
        padding,
        cursor: onClick ? 'pointer' : 'default',
        ...style,
      }}
    >
      {children}
    </As>
  );
});

// -----------------------------------------------------------------
// ScoreDial — conic-gradient ring + inner disc.
// On light surfaces: outer raised disc. On navy: gold ring + navy well.
// -----------------------------------------------------------------
export function ScoreDial({ pct = 0, size = 150, label, variant = 'light', testid }) {
  const safePct = Math.max(0, Math.min(100, pct));
  const ring = Math.round(size * 0.853);
  const inner = Math.round(size * 0.72);

  if (variant === 'navy') {
    return (
      <div
        data-testid={testid}
        style={{
          width: size,
          height: size,
          borderRadius: '50%',
          background: `conic-gradient(${V3.gold} 0 ${safePct}%, rgba(255,255,255,.12) ${safePct}% 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <div
          style={{
            width: inner,
            height: inner,
            borderRadius: '50%',
            background: V3.navyDeep,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
          }}
        >
          <div style={{ fontFamily: V3.mono, fontSize: Math.max(24, size * 0.22), fontWeight: 700 }}>{Math.round(safePct)}</div>
          {label && <div style={{ fontSize: 11, color: V3.gold, textTransform: 'uppercase', letterSpacing: '0.12em' }}>{label}</div>}
        </div>
      </div>
    );
  }

  // light variant — raised outer disc
  return (
    <div
      data-testid={testid}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: V3.surface,
        boxShadow: V3.raised,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <div
        style={{
          width: ring,
          height: ring,
          borderRadius: '50%',
          background: `conic-gradient(${V3.navy} 0 ${safePct}%, var(--v3-track) ${safePct}% 100%)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <div
          style={{
            width: inner,
            height: inner,
            borderRadius: '50%',
            background: 'linear-gradient(145deg, #f7f8fb, #e2e5ed)',
            boxShadow: V3.raisedSm,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            color: V3.navy,
          }}
        >
          <div style={{ fontFamily: V3.mono, fontSize: Math.max(22, size * 0.2), fontWeight: 700 }}>{Math.round(safePct)}</div>
          {label && <div style={{ fontSize: 10.5, color: V3.muted, textTransform: 'uppercase', letterSpacing: '0.12em', marginTop: 2 }}>{label}</div>}
        </div>
      </div>
    </div>
  );
}

export function MiniScoreDial({ pct = 0, size = 60 }) {
  const inner = Math.round(size * 0.8);
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: `conic-gradient(${V3.navy} 0 ${pct}%, var(--v3-track) ${pct}% 100%)`,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <div
        style={{
          width: inner,
          height: inner,
          borderRadius: '50%',
          background: V3.surface,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: V3.mono,
          fontSize: 15,
          fontWeight: 700,
          color: V3.navy,
        }}
      >
        {Math.round(pct)}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------
// SeverityTile — 48px rounded square filled with P1/P2/P3 color
// -----------------------------------------------------------------
export function SeverityTile({ severity = 'P2', number = 0, size = 48 }) {
  const color = severity === 'P1' ? V3.p1 : severity === 'P3' ? V3.p3 : V3.p2;
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: 16,
        background: color,
        color: '#fff',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: V3.mono,
        fontSize: 18,
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {String(number).padStart(2, '0')}
    </div>
  );
}

// -----------------------------------------------------------------
// StatusPill — tint bg, colored dot, bold text
// -----------------------------------------------------------------
export function StatusPill({ children, dot = V3.navy, bg }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        background: bg || V3.tint,
        borderRadius: 999,
        padding: '5px 11px',
        fontSize: 12.5,
        fontWeight: 600,
        color: V3.navy,
        whiteSpace: 'nowrap',
      }}
    >
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          background: dot,
          flexShrink: 0,
        }}
      />
      {children}
    </span>
  );
}

// -----------------------------------------------------------------
// Toast — navy pill at top center with gold check circle
// -----------------------------------------------------------------
export function Toast({ children, visible = false }) {
  if (!visible) return null;
  return (
    <div
      style={{
        position: 'fixed',
        top: 24,
        left: '50%',
        transform: 'translateX(-50%)',
        background: V3.navy,
        color: '#fff',
        padding: '10px 20px 10px 10px',
        borderRadius: 999,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 10,
        fontSize: 14,
        fontWeight: 600,
        zIndex: 100,
        boxShadow: V3.shadowNavyBtn || '0 12px 24px -12px rgba(20,38,84,.85)',
      }}
    >
      <span
        style={{
          width: 28,
          height: 28,
          borderRadius: '50%',
          background: V3.gold,
          color: V3.navy,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <PhIcon name="check" size={16} />
      </span>
      {children}
    </div>
  );
}

// -----------------------------------------------------------------
// OfflinePill — gold floating pill at top center
// -----------------------------------------------------------------
export function OfflinePill({ count = 0 }) {
  return (
    <div
      style={{
        position: 'fixed',
        top: 16,
        left: '50%',
        transform: 'translateX(-50%)',
        background: V3.gold,
        color: V3.navy,
        padding: '10px 18px',
        borderRadius: 999,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 13,
        fontWeight: 700,
        zIndex: 90,
        boxShadow: '0 10px 20px -10px rgba(20,38,84,.5)',
      }}
    >
      <PhIcon name="cloud-slash" size={16} />
      Offline · {count} changes waiting to sync
    </div>
  );
}

// -----------------------------------------------------------------
// InsetInput — the standard field: 56px tall, radius 18, inset shadow
// -----------------------------------------------------------------
export function InsetInput({ value, onChange, placeholder, type = 'text', testid, autoComplete, onKeyDown }) {
  return (
    <input
      type={type}
      value={value || ''}
      onChange={(e) => onChange?.(e.target.value)}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      data-testid={testid}
      autoComplete={autoComplete}
      style={{
        height: 56,
        width: '100%',
        border: 0,
        borderRadius: 18,
        background: V3.surface,
        boxShadow: V3.insetSm,
        padding: '0 18px',
        fontSize: 15.5,
        color: V3.ink,
        outline: 'none',
      }}
    />
  );
}

export function InsetTextArea({ value, onChange, placeholder, rows = 4, testid, minHeight = 136 }) {
  return (
    <textarea
      value={value || ''}
      onChange={(e) => onChange?.(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      data-testid={testid}
      style={{
        width: '100%',
        minHeight,
        border: 0,
        borderRadius: 22,
        background: V3.surface,
        boxShadow: V3.insetSm,
        padding: '16px 18px',
        fontSize: 15,
        color: V3.ink,
        outline: 'none',
        resize: 'vertical',
        fontFamily: 'inherit',
      }}
    />
  );
}

// -----------------------------------------------------------------
// Eyebrow — small uppercase label used above titles everywhere
// -----------------------------------------------------------------
export function Eyebrow({ children, color = V3.gold, size = 11 }) {
  return (
    <div
      style={{
        fontFamily: V3.mono,
        fontSize: size,
        fontWeight: 700,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        color,
      }}
    >
      {children}
    </div>
  );
}

// -----------------------------------------------------------------
// NavRail — floating raised nav, 84px iPad / 232px desktop / 70px pill iPhone
// -----------------------------------------------------------------
export function NavRail({ items, active, onNavigate, device = 'desktop', user }) {
  // items: [{ id, icon, label }]
  if (device === 'iphone') {
    return (
      <nav
        style={{
          position: 'fixed',
          bottom: 16,
          left: 16,
          right: 16,
          height: 70,
          background: V3.navy,
          borderRadius: 999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-around',
          padding: '0 20px',
          zIndex: 50,
          boxShadow: '0 12px 24px -12px rgba(20,38,84,.6)',
        }}
      >
        {items.map((it) => {
          const isActive = it.id === active;
          return (
            <button
              key={it.id}
              type="button"
              onClick={() => onNavigate?.(it.id)}
              style={{
                background: isActive ? V3.gold : 'transparent',
                color: isActive ? V3.navy : '#fff',
                width: 48,
                height: 48,
                borderRadius: '50%',
                border: 0,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                opacity: isActive ? 1 : 0.85,
              }}
            >
              <PhIcon name={it.icon} size={22} />
            </button>
          );
        })}
      </nav>
    );
  }

  const isDesk = device === 'desktop';
  const navW = isDesk ? 232 : 84;
  return (
    <nav
      style={{
        position: 'fixed',
        top: 20,
        bottom: 20,
        left: 20,
        width: navW,
        borderRadius: 30,
        background: V3.surface,
        boxShadow: V3.raisedLg,
        display: 'flex',
        flexDirection: 'column',
        alignItems: isDesk ? 'stretch' : 'center',
        padding: isDesk ? '20px 14px' : '20px 10px',
        gap: 12,
        zIndex: 40,
      }}
    >
      {isDesk && (
        <div style={{ padding: '0 10px 8px', marginBottom: 6 }}>
          <div style={{ fontFamily: V3.mono, fontSize: 11, letterSpacing: '0.18em', color: V3.gold, fontWeight: 700, textTransform: 'uppercase' }}>GigLine</div>
          <div style={{ fontSize: 17, fontWeight: 700, color: V3.navy, letterSpacing: '-0.02em' }}>Field</div>
        </div>
      )}
      {items.map((it) => {
        const isActive = it.id === active;
        return (
          <button
            key={it.id}
            type="button"
            onClick={() => onNavigate?.(it.id)}
            data-testid={`nav-${it.id}`}
            style={{
              height: 56,
              width: isDesk ? 'auto' : 56,
              alignSelf: isDesk ? 'stretch' : 'center',
              borderRadius: isDesk ? 18 : '50%',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: isDesk ? 'flex-start' : 'center',
              gap: 14,
              padding: isDesk ? '0 16px' : 0,
              cursor: 'pointer',
              background: isActive ? V3.navy : 'transparent',
              color: isActive ? '#fff' : V3.muted,
              border: 0,
              fontSize: 22,
              boxShadow: isActive ? '0 10px 20px -10px rgba(20,38,84,.8)' : 'none',
            }}
          >
            <PhIcon name={it.icon} size={22} />
            {isDesk && <span style={{ fontSize: 15, fontWeight: 600 }}>{it.label}</span>}
          </button>
        );
      })}
      <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', gap: 12, justifyContent: isDesk ? 'flex-start' : 'center', padding: isDesk ? '0 8px' : 0 }}>
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            background: V3.tint,
            color: V3.navy,
            fontSize: 15,
            fontWeight: 700,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {user?.initials || 'VL'}
        </div>
        {isDesk && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: V3.ink }}>{user?.name || 'Vince Lawrence'}</span>
            <span style={{ fontSize: 12, color: V3.muted }}>{user?.role || 'Consultant'}</span>
          </div>
        )}
      </div>
    </nav>
  );
}

// -----------------------------------------------------------------
// Page shell — gives you the correct background + nav rail wiring
// -----------------------------------------------------------------
export function V3Page({ children, device = 'desktop', showNav = true, navItems, navActive, onNavigate, user, offline = 0 }) {
  const padLeft = !showNav ? 0 : device === 'desktop' ? 272 : device === 'ipad' ? 124 : 0;
  const padBottom = device === 'iphone' && showNav ? 102 : 0;
  return (
    <div
      style={{
        minHeight: '100vh',
        background: V3.surface,
        paddingLeft: padLeft,
        paddingBottom: padBottom,
        paddingTop: 20,
        paddingRight: 20,
      }}
    >
      {offline > 0 && <OfflinePill count={offline} />}
      {showNav && (
        <NavRail
          items={navItems}
          active={navActive}
          onNavigate={onNavigate}
          device={device}
          user={user}
        />
      )}
      <div style={{ maxWidth: 1240, margin: '0 auto', padding: '20px 16px 40px' }}>
        {children}
      </div>
    </div>
  );
}
